import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

type NoShowNoticeProps = ComponentPropsWithoutRef<typeof FlexColumn> & {
  /** The patient's first name, to address them; left out when unknown. */
  givenName?: string
}

/**
 * The notice on an appointment the patient did not attend: the warning title, then the hospital's
 * explanation of why to cancel in time and what a no-show costs.
 */
const NoShowNotice = ({ givenName, ...props }: NoShowNoticeProps) => {
  const theme = useTheme()

  return (
    <FlexColumn gap={theme.UI.Spacing.Space3} {...props}>
      <Flex alignItems='center' gap={theme.UI.Spacing.Space2}>
        <FontAwesomeIcon icon={['far', 'triangle-exclamation']} color={theme.Colors.Status.Error} />
        <Span type='BodyLargeBold' color={theme.Colors.Status.Error}>
          {t({
            id: 'appointments.detail.noshow.title',
            message: 'Je was niet op je afspraak',
          })}
        </Span>
      </Flex>
      {/* Only greet by name when there is one; an empty placeholder would leave a lone comma */}
      {givenName ? (
        <Span type='BodyMediumRegular'>
          {t({
            id: 'appointments.detail.noshow.description.header',
            message: '{name},',
            values: { name: givenName },
          })}
        </Span>
      ) : null}
      <Span type='BodyMediumRegular'>
        {t({
          id: 'appointments.detail.noshow.description.body1',
          message:
            'Was je verhinderd? Contacteer dan steeds het secretariaat van je zorgverlener. Zo kunnen we andere patiënten helpen en onze dienstverlening efficiënt organiseren.',
        })}
      </Span>
      <Span type='BodyMediumRegular'>
        {t({
          id: 'appointments.detail.noshow.description.body2',
          message:
            'Doe je dat minder dan 24 uur voor je geplande afspraak of niet? Dan zal je een forfaitaire kost van 25 euro aangerekend worden. Eventuele uitzonderingen op deze regel staan vermeld op de afsprakenpagina van de dienst op onze website.',
        })}
      </Span>
      <Span type='BodyMediumRegular'>
        {t({
          id: 'appointments.detail.noshow.description.body3',
          message: 'Bedankt voor je begrip.',
        })}
      </Span>
    </FlexColumn>
  )
}

export { NoShowNotice }
